"use client";

import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { usePathname } from "next/navigation";
import Link from "next/link";
import RoomCard from "./RoomCard";
import Divider from "./Divider";
import type { RoomListItem } from "../types/room";
import { getLocaleFromPath } from "../lib/i18n/utils";

type RoomsSectionProps = {
  rooms: RoomListItem[];
  headingLevel?: "h1" | "h2";
};

export default function RoomsSection({ rooms, headingLevel = "h1" }: RoomsSectionProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const HeadingTag = headingLevel;
  
  return (
    <section
      className="mx-auto px-5 pt-19 pb-14 text-center bg-[linear-gradient(to_bottom,#ffffff_11%,#1E2E25_11%)] 
      lg:bg-[linear-gradient(to_bottom,#ffffff_30%,#1E2E25_30%)]
      xl:bg-[linear-gradient(to_bottom,#ffffff_50%,#1E2E25_50%)]"
    >
      <HeadingTag className="text-[3rem] font-normal leading-[100%] tracking-normal text-ui-card">
        {t("Rooms")}
      </HeadingTag>

      <Divider />

      <p className="mx-auto mt-6 max-w-3xl text-sm font-light leading-6 text-ui-card/70">
        <Trans
          i18nKey={`Our <0>guesthouse</0>'s hospitable <1>rooms</1>, decorated with the experienced eye of Agla Papadimitriou-Lioga, promise a pleasant and comfortable stay and guarantee the absolute satisfaction of every demanding guest.`}
          components={[
            <Link
              key="guesthouse-link"
              href={locale === "el" ? "/" : `/${locale}`}
              className="underline hover:text-ui-card transition-colors decoration-ui-card/50 hover:decoration-ui-card"
            />,
            <Link
              key="rooms-link"
              href={locale === "el" ? "/rooms" : `/${locale}/rooms`}
              className="underline hover:text-ui-card transition-colors decoration-ui-card/50 hover:decoration-ui-card"
            />,
          ]}
        />
      </p>

      <div className="mx-auto max-w-480 md:px-5 mt-25">
        <div className="grid grid-cols-1 gap-y-16 lg:grid-cols-2 xl:grid-cols-4 lg:gap-x-19.25">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              slug={room.slug}
              title={room.title}
              size={room.areaM2}
              people={room.capacityAdults}
              beds={room.beds}
              image={room.heroImage?.url || null}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

